/**
 * Aufgaben (Spec P2) — Kanban-Board: Offen · In Arbeit · Erledigt.
 * Karten mit Titel, Priorität, Fälligkeit, verknüpftem Vorgang und Zuständigem.
 * Status per Pfeil verschieben; Anlegen-Drawer. Alles aus echten API-Daten.
 */
import { useMemo, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { useApi, apiMutate } from "../hooks/useApi";
import type { EventRow, Member, Task, TaskStatus } from "../ui/types";
import { PILL_CLASSES, formatDate } from "../ui/format";
import { Drawer, EmptyState, ErrorNote, Icon, LoadingRows, PageHeader } from "../ui/components";

const COLUMNS: { key: TaskStatus; label: string; icon: string }[] = [
  { key: "open", label: "Offen", icon: "radio_button_unchecked" },
  { key: "doing", label: "In Arbeit", icon: "pending" },
  { key: "done", label: "Erledigt", icon: "check_circle" },
];
const NEXT: Record<TaskStatus, TaskStatus | null> = { open: "doing", doing: "done", done: null };
const PREV: Record<TaskStatus, TaskStatus | null> = { open: null, doing: "open", done: "doing" };
/** I7: 1c-Statustokens statt rohem Tailwind (bg-red-100 etc.). */
const PRIO: Record<string, { label: string; cls: string }> = {
  high: { label: "Hoch", cls: PILL_CLASSES.danger },
  normal: { label: "Normal", cls: PILL_CLASSES.neutral },
  low: { label: "Niedrig", cls: PILL_CLASSES.info },
};

function shortId(s: string | null): string {
  return s ? s.slice(0, 8) : "";
}

export default function TasksPage() {
  const tasksQ = useApi<Task[]>("/api/tasks");
  const eventsQ = useApi<EventRow[]>("/api/events");
  const membersQ = useApi<Member[]>("/api/members");
  const [createOpen, setCreateOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const tasks = tasksQ.data ?? [];
  const events = eventsQ.data ?? [];
  const members = membersQ.data ?? [];
  const eventLabel = (id: string | null) => {
    if (!id) return null;
    const e = events.find((x) => x.id === id);
    return e ? e.number ?? e.occasion ?? shortId(id) : shortId(id);
  };

  const byStatus = useMemo(() => {
    const m: Record<TaskStatus, Task[]> = { open: [], doing: [], done: [] };
    for (const t of tasks) m[t.status]?.push(t);
    return m;
  }, [tasks]);

  async function move(t: Task, status: TaskStatus) {
    try {
      await apiMutate(`/api/tasks/${t.id}`, "PATCH", { status });
      tasksQ.reload();
    } catch (e) {
      setFormError(e instanceof Error ? e.message : String(e));
    }
  }
  async function remove(t: Task) {
    try {
      await apiMutate(`/api/tasks/${t.id}`, "DELETE");
      tasksQ.reload();
    } catch (e) {
      setFormError(e instanceof Error ? e.message : String(e));
    }
  }

  async function handleCreate(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = new FormData(e.currentTarget);
    setBusy(true);
    setFormError(null);
    try {
      await apiMutate("/api/tasks", "POST", {
        title: form.get("title"),
        eventId: (form.get("eventId") as string) || null,
        assignee: (form.get("assignee") as string) || null,
        dueDate: (form.get("dueDate") as string) || null,
        priority: (form.get("priority") as string) || "normal",
        notes: (form.get("notes") as string) || null,
      });
      setCreateOpen(false);
      tasksQ.reload();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <PageHeader
        title="Aufgaben"
        subtitle="Wer macht was bis wann – über alle Vorgänge hinweg."
        actions={
          <button type="button" className="btn-primary" onClick={() => { setFormError(null); setCreateOpen(true); }}>
            <Icon name="add" className="text-[18px]" /> Aufgabe
          </button>
        }
      />

      {(tasksQ.error || formError) && <ErrorNote message={tasksQ.error ?? formError ?? ""} />}
      {tasksQ.loading ? (
        <LoadingRows rows={6} />
      ) : tasks.length === 0 ? (
        <div className="card">
          <EmptyState
            icon="checklist"
            title="Noch keine Aufgaben"
            hint="Legen Sie die erste Aufgabe an."
            cta={{ label: "Aufgabe anlegen", icon: "add", onClick: () => { setFormError(null); setCreateOpen(true); } }}
          />
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
          {COLUMNS.map((col) => (
            <div key={col.key} className="rounded-2xl bg-surface-container-low/60 p-3 dark:bg-surface-variant/10">
              <div className="mb-3 flex items-center justify-between px-2">
                <span className="flex items-center gap-2 font-label-md text-label-md font-semibold text-on-background dark:text-inverse-on-surface">
                  <Icon name={col.icon} className={`text-[18px] ${col.key === "done" ? "text-green-600" : "text-primary"}`} /> {col.label}
                </span>
                <span className="rounded-full bg-surface-container-high px-2 py-0.5 font-label-md text-label-md text-on-surface-variant dark:bg-surface-variant/30">
                  {byStatus[col.key].length}
                </span>
              </div>
              <div className="space-y-2">
                {byStatus[col.key].map((t) => (
                  <div key={t.id} className="group rounded-xl border border-outline-variant/30 bg-surface-container-lowest p-3 shadow-sm dark:border-outline/30 dark:bg-inverse-surface">
                    <div className="flex items-start justify-between gap-2">
                      <p className={`font-body-md text-body-md font-medium text-on-background dark:text-inverse-on-surface ${t.status === "done" ? "line-through opacity-60" : ""}`}>
                        {t.title}
                      </p>
                      <button type="button" className="btn-ghost !px-1 opacity-0 transition-opacity group-hover:opacity-100" title="Löschen" onClick={() => void remove(t)}>
                        <Icon name="close" className="text-[16px]" />
                      </button>
                    </div>
                    <div className="mt-2 flex flex-wrap items-center gap-1.5">
                      <span className={`rounded px-1.5 py-0.5 font-label-md text-[10px] ${PRIO[t.priority]!.cls}`}>{PRIO[t.priority]!.label}</span>
                      {t.dueDate && (
                        <span className="inline-flex items-center gap-1 font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
                          <Icon name="event" className="text-[14px]" /> {formatDate(t.dueDate)}
                        </span>
                      )}
                      {t.eventId && (
                        <Link to={`/veranstaltungen/${t.eventId}`} className="inline-flex items-center gap-1 font-body-sm text-body-sm text-primary hover:underline">
                          <Icon name="link" className="text-[14px]" /> {eventLabel(t.eventId)}
                        </Link>
                      )}
                      {t.assignee && (
                        <span className="inline-flex items-center gap-1 font-body-sm text-body-sm text-on-surface-variant" title={t.assignee}>
                          <Icon name="person" className="text-[14px]" /> {shortId(t.assignee)}
                        </span>
                      )}
                    </div>
                    <div className="mt-2 flex justify-between">
                      <button type="button" className="btn-ghost !px-1.5 !py-0.5 text-on-surface-variant disabled:opacity-30" disabled={!PREV[t.status]} onClick={() => PREV[t.status] && void move(t, PREV[t.status]!)} title="Zurück">
                        <Icon name="chevron_left" className="text-[18px]" />
                      </button>
                      <button type="button" className="btn-ghost !px-1.5 !py-0.5 text-primary disabled:opacity-30" disabled={!NEXT[t.status]} onClick={() => NEXT[t.status] && void move(t, NEXT[t.status]!)} title="Weiter">
                        <Icon name="chevron_right" className="text-[18px]" />
                      </button>
                    </div>
                  </div>
                ))}
                {byStatus[col.key].length === 0 && <p className="px-2 py-4 text-center font-body-sm text-body-sm text-on-surface-variant/50">—</p>}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Anlegen */}
      <Drawer open={createOpen} onClose={() => setCreateOpen(false)} title="Aufgabe anlegen">
        <form className="space-y-4" onSubmit={(e) => void handleCreate(e)}>
          {formError && <ErrorNote message={formError} />}
          <Field label="Titel *"><input name="title" required className="input" /></Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Priorität">
              <select name="priority" className="input" defaultValue="normal">
                <option value="low">Niedrig</option>
                <option value="normal">Normal</option>
                <option value="high">Hoch</option>
              </select>
            </Field>
            <Field label="Fällig am"><input name="dueDate" type="date" className="input" /></Field>
          </div>
          <Field label="Vorgang (optional)">
            <select name="eventId" className="input" defaultValue="">
              <option value="">— keiner —</option>
              {events.map((e) => (
                <option key={e.id} value={e.id}>{e.number ?? e.occasion ?? shortId(e.id)}</option>
              ))}
            </select>
          </Field>
          <Field label="Zuständig (optional)">
            <select name="assignee" className="input" defaultValue="">
              <option value="">— niemand —</option>
              {members.map((m) => (
                <option key={m.stackUserId} value={m.stackUserId}>{shortId(m.stackUserId)} · {m.role}</option>
              ))}
            </select>
          </Field>
          <Field label="Notizen"><textarea name="notes" className="input min-h-[70px]" /></Field>
          <div className="flex justify-end gap-3 pt-1">
            <button type="button" className="btn-secondary" onClick={() => setCreateOpen(false)}>Abbrechen</button>
            <button type="submit" className="btn-primary" disabled={busy}>{busy ? "Speichert …" : "Aufgabe anlegen"}</button>
          </div>
        </form>
      </Drawer>
    </>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1.5 block font-body-sm text-body-sm font-semibold text-on-background dark:text-inverse-on-surface">{label}</span>
      {children}
    </label>
  );
}
